import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { TableSegment } from '@anytag/shared-atoms';
import { cn, formatNumber } from '@anytag/shared-utils';
import { YoutubeCmsConfirmedChannelSummary, YoutubeCmsEstimateChannelSummary } from '@anytag/graphql';
import { YoutubeCMSRevenueType, YoutubeCMSSegmentMode } from '../types';

export interface YoutubeCMSSummaryProps {
  className?: string;
  type: YoutubeCMSRevenueType;
  segment?: YoutubeCMSSegmentMode;
  data?: YoutubeCmsConfirmedChannelSummary &
    Partial<Pick<YoutubeCmsEstimateChannelSummary, 'rpm' | 'taRpm' | 'taRevenue' | 'taProfit'>>;
  onSegmentChange?: (segment: YoutubeCMSSegmentMode) => void;
}

export const YoutubeCMSSummary = ({ data, type, segment, className, onSegmentChange }: YoutubeCMSSummaryProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();

  const currency = t(data?.currency || '');
  const isConfirmedRevenue = type === YoutubeCMSRevenueType.CONFIRMED_REVENUE;
  const summaries = [
    {
      unit: currency,
      value: formatNumber(data?.rpm),
      label: t('HeaderColumn.Estimated RPM'),
      hidden: !isAdminStaff || isConfirmedRevenue
    },
    {
      unit: currency,
      hidden: isConfirmedRevenue,
      value: formatNumber(data?.taRpm),
      label: t('HeaderColumn.Estimated talent agency RPM')
    },
    {
      unit: currency,
      hidden: !isAdminStaff,
      value: formatNumber(data?.partnerRevenue),
      label: t(isConfirmedRevenue ? 'HeaderColumn.Confirmed partner revenue' : 'HeaderColumn.Estimated partner revenue')
    },
    {
      unit: currency,
      hidden: isConfirmedRevenue,
      value: formatNumber(data?.taRevenue),
      label: t('HeaderColumn.Estimated talent agency revenue')
    },
    {
      unit: currency,
      value: formatNumber(data?.shareCreator),
      label: t(
        isConfirmedRevenue ? 'HeaderColumn.Confirmed share to creator' : 'HeaderColumn.Estimated share to creator'
      )
    },
    {
      unit: currency,
      hidden: !isAdminStaff,
      value: formatNumber(data?.profit),
      label: t(isConfirmedRevenue ? 'HeaderColumn.Confirmed gross profit' : 'HeaderColumn.Estimated GP')
    },
    {
      unit: currency,
      hidden: isConfirmedRevenue,
      value: formatNumber(data?.taProfit),
      label: t('HeaderColumn.Estimated talent agency GP')
    }
  ].filter((summary) => !summary.hidden);

  return (
    <div className={cn('box-border border-t-border bg-white px-4 py-6', className)}>
      <div className="flex flex-wrap gap-y-4">
        {summaries.map((summary, index) => (
          <div key={index} className="box-border basis-1/4 border-l-2 border-border px-3 font-semibold">
            <div className="text-sm leading-5 uppercase">{summary.label}</div>
            <div className="flex items-baseline pr-3 leading-6">
              <div className="text-2xl">{summary.value}</div>
              {!!summary.unit && <div className="ml-1.5 text-sm">{summary.unit}</div>}
            </div>
          </div>
        ))}
      </div>

      {segment && onSegmentChange ? (
        <TableSegment
          type="radio"
          className="mt-6"
          values={[segment]}
          title={t('Segment')}
          onChange={onSegmentChange}
          options={[
            {
              label: t('Show by channel'),
              value: YoutubeCMSSegmentMode.CHANNELS
            },
            {
              label: t('Show by asset'),
              value: YoutubeCMSSegmentMode.ASSETS
            }
          ]}
        />
      ) : null}
    </div>
  );
};
